import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Container, Stack } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridPagination } from '@mui/x-data-grid';

import PageHeader from '~/components/Common/Elements/PageHeader';

import CorporateEventsService, { Event } from '~/services/CorporateEventsService';

export default function EventsListPage() {
  const history = useHistory();

  const {
    data: events = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['corporate-events', 'events'],
    queryFn: () => CorporateEventsService.getEvents(),
  });

  const handleEdit = (id: string) => {
    history.push(`/corporate-events/events/${id}/edit`);
  };

  const handleCreate = () => {
    history.push('/corporate-events/events/create');
  };

  const handleManageCompanies = () => {
    history.push('/corporate-events/companies');
  };

  const columns: Array<GridColDef<Event>> = [
    {
      field: 'name',
      headerName: 'Event Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'hotelName',
      headerName: 'Hotel',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'locationCity',
      headerName: 'Location',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => {
        if (row.locationCity && row.locationCountry) {
          return `${row.locationCity}, ${row.locationCountry}`;
        }
        return row.locationCity || row.locationCountry || '';
      },
    },
    {
      field: 'checkinDate',
      headerName: 'Check-in',
      width: 120,
      valueGetter: (value) => {
        if (!value) {
          return '';
        }
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'checkoutDate',
      headerName: 'Check-out',
      width: 120,
      valueGetter: (value) => {
        if (!value) {
          return '';
        }
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'isPublicLink',
      headerName: 'Public Link',
      width: 120,
      type: 'boolean',
    },
    {
      field: 'allowEditRegistration',
      headerName: 'Allow Edit',
      width: 120,
      type: 'boolean',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEdit(params.id as string)}
        />,
      ],
    },
  ];

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Corporate Events</title>
      </Helmet>

      <PageHeader title="Corporate Events">
        <Stack direction="row" spacing={2}>
          <Button variant="text" startIcon={<BusinessIcon />} onClick={handleManageCompanies}>
            Manage Companies
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
            Create Event
          </Button>
        </Stack>
      </PageHeader>

      <Box mt={4}>
        {error && (
          <Box mb={2} p={2} sx={{ backgroundColor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            Error loading events: {error instanceof Error ? error.message : 'Unknown error'}
          </Box>
        )}

        <DataGrid
          loading={isLoading}
          rows={events}
          columns={columns}
          pagination
          paginationModel={{ page: 0, pageSize: 25 }}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowId={(row) => row.id}
          slots={{ pagination: GridPagination }}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>
    </Container>
  );
}
